import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AdvantagesSection.module.css';
import useScrollAnimation from '../../hooks/useScrollAnimation';

// SVG Icon Components
const ConnectivityIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
    </svg>
);

const DataOwnershipIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h3.75c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-3.75A1.125 1.125 0 0 1 3 16.875v-3.75ZM3 7.875C3 7.254 3.504 6.75 4.125 6.75h3.75c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-3.75A1.125 1.125 0 0 1 3 10.5v-3.75Zm0 0h18M3 16.875h18m-6.75-10.125h.008v.008h-.008v-.008Zm0 0H21m0 0v9.375m0-9.375h.008v.008h-.008V6.75Zm-6.75 0h.008v.008h-.008v-.008Zm0 0h.008v.008h-.008V6.75Zm0 0H9.75m9.375 0H9.75M3 7.875h1.125M3 13.125h1.125m0 0v3.75m0-3.75h3.75M3 16.875h1.125m9.375 0h.008v.008h-.008v-.008Zm0 0h.008v.008h-.008v-.008Zm0 0h.008v.008h-.008v-.008Zm0 0h.008v.008h-.008V16.875Z" />
    </svg>
);

const AutomationIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z" />
    </svg>
);

const PersonalApproachIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25A2.25 2.25 0 0 1 5.25 3h4.5M21 3h-4.5m-4.5 0H9.75M5.25 3h4.5M12 3v3.75" />
    </svg>
);

// New Icon for "Fits any machine"
const FitsAnyMachineIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.474-2.474a1.293 1.293 0 00-1.828-1.828L9.6 13.34l-2.475 2.475M11.42 15.17L9.6 13.34m0 0L3.75 7.5A2.652 2.652 0 017.5 3.75l5.877 5.877m0 0l2.474 2.474" />
    </svg>
);

// New Icon for "More than yield"
const MoreThanYieldIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h12A2.25 2.25 0 0020.25 14.25V3M3.75 3h16.5M3.75 3v1.5m16.5-1.5v1.5m-16.5 0L12 12.75l8.25-8.25M12 12.75v3.75" />
    </svg>
);

const advantagesKeyMap = [
    {
        Icon: ConnectivityIcon,
        titleKey: 'advantages.connectivity.title',
        descriptionKey: 'advantages.connectivity.description'
    },
    {
        Icon: DataOwnershipIcon,
        titleKey: 'advantages.dataOwnership.title',
        descriptionKey: 'advantages.dataOwnership.description'
    },
    {
        Icon: AutomationIcon,
        titleKey: 'advantages.automation.title',
        descriptionKey: 'advantages.automation.description'
    },
    {
        Icon: PersonalApproachIcon,
        titleKey: 'advantages.personalApproach.title',
        descriptionKey: 'advantages.personalApproach.description'
    },
    {
        Icon: FitsAnyMachineIcon,
        titleKey: 'advantages.fitsAnyMachine.title',
        descriptionKey: 'advantages.fitsAnyMachine.description'
    },
    {
        Icon: MoreThanYieldIcon,
        titleKey: 'advantages.moreThanYield.title',
        descriptionKey: 'advantages.moreThanYield.description'
    }
];

const AdvantageCard = ({ Icon, titleKey, descriptionKey }) => {
    const { t } = useTranslation();
    const cardRef = useScrollAnimation({ once: true });
    return (
        <div className={`${styles.advantageCard} animate-on-scroll`} ref={cardRef}>
            <div className={styles.iconPlaceholder}><Icon /></div>
            <h3>{t(titleKey)}</h3>
            <p>{t(descriptionKey)}</p>
        </div>
    );
};

const AdvantagesSection = () => {
    const { t } = useTranslation();
    const sectionRef = useScrollAnimation({ once: true });

    return (
        <section id="benefits" className={`${styles.benefitsSection} animate-on-scroll`} ref={sectionRef}>
            <div className="container">
                <h2 className={`section-title ${styles.sectionTitle}`}>{t('advantages.sectionTitle')}</h2>
                <div className={styles.advantagesGrid}>
                    {advantagesKeyMap.map(advantage => (
                        <AdvantageCard key={advantage.titleKey} Icon={advantage.Icon} titleKey={advantage.titleKey} descriptionKey={advantage.descriptionKey} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AdvantagesSection;
